import { defineComponent as _defineComponent } from 'vue'
import SimilarTitles from '@/components/SimilarTitles.vue';
import BuyBoxBanner from '@/features/buybox/components/BuyBoxBanner.vue';
import ListBuyBox from '@/features/buybox/components/list/ListBuyBox.vue';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';
import { useI18n } from '@/helpers/composables/useI18n';
import { useLanguageStore, useUserStore } from '@/helpers/composables/useStores';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import { type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';
import SectionContainer from '@/pages/titleDetailV1/components/SectionContainer.vue';
import TitleDetailJumpLinksV1 from '@/pages/titleDetailV1/v1/TitleDetailJumpLinksV1.vue';
import TitleDetailTitleClipsV1 from '@/pages/titleDetailV1/v1/TitleDetailTitleClipsV1.vue';
import {
	isTitleSeason,
	isTitleShow,
	useTitleDetailInfo,
} from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import TitleDetailHeroV1 from '@/pages/titleDetailV1/v1/hero/TitleDetailHeroV1.vue';
import TitleDetailTitleSidebarV1 from '@/pages/titleDetailV1/v1/sidebar/TitleDetailTitleSidebarV1.vue';

import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';
import { NewBuyBoxView } from '@/stores/user.store';
import { computed, defineAsyncComponent, provide, ref } from 'vue';

import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import GridBuyBox from '@/features/buybox/components/grid/GridBuyBox.vue';
import PromotedGridBuyBox from '@/features/buybox/components/grid/PromotedGridBuyBox.vue';
import { TextRecommendationModalV2 } from '@/lazy-load-components';
import TextRecommendations from '@/features/why-to-watch/components/TextRecommendations.vue';
import SeasonListV1 from '@/pages/titleDetailV1/v1/SeasonListV1.vue';

import { ModuleContentStreamingCharts, MonetizationType, ObjectType, ProfileType } from '@/@types/graphql-types';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { extractBuyboxOffers } from '@/helpers/title-helper';

import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useWtwTracking } from '@/features/why-to-watch/tracking';
import StreamingOffersHistory from '@/features/streaming-offers-history/components/StreamingOffersHistory.vue';
import { useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { useActivateExperiment } from '@/libraries/experiment';
import { useBuyboxView } from '@/features/buybox/composables/useBuyboxView';
import { useArbitrage } from '@/helpers/composables/useArbitrage';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailV1',
  props: {
    titleDetailNode: null,
    promotedOffers: { default: () => [] },
    metaData: null,
    genresByShortName: null,
    hidePromotedBuyBox: { type: Boolean },
    _sponsoredAd: { default: undefined },
    entityId: null,
    isLoading: { type: Boolean },
    isPerceivedLoading: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  emits: ['buyboxLoading', 'closeTitleDetailModal'],
  setup(__props: any, { emit: emits }) {

const props = __props as { titleDetailNode: AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Movie' | 'Show' | 'Season'> | undefined, promotedOffers: TitleOfferFragment[], metaData: any, genresByShortName: any, hidePromotedBuyBox: boolean, _sponsoredAd: SponsoredAdFragment | undefined, entityId?: string | undefined, isLoading?: boolean, isPerceivedLoading: boolean, additionalContexts: SnowplowContext[] };

const TVODHeroSection = defineAsyncComponent(
	() => import('@/components/experiments/JWTVCinematic/components/TVODHeroSection.vue')
);
const AVODHeroSection = defineAsyncComponent(
	() => import('@/components/experiments/JWTVCinematic/components/AVODHeroSection.vue')
);

const TitleDetailStreamingChartV2 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/streaming-chart/TitleDetailStreamingChartV2.vue')
);
const TitleSynopsis = defineAsyncComponent(() => import('@/components/details/TitleSynopsis.vue'));

const ArticleBlock = defineAsyncComponent(() => import('@/components/details/editorial/ArticleBlock.vue'));
const UpcomingContent = defineAsyncComponent(() => import('@/components/upcoming/UpcomingContent.vue'));
const EpisodesList = defineAsyncComponent(() => import('@/components/EpisodesList.vue'));

const TitleCredits = defineAsyncComponent(() => import('@/components/details/TitleCredits.vue'));
const SpinningText = defineAsyncComponent(() => import('@/components/details/SpinningText.vue'));
const TitleDetailSimilarTitlesV2 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailSimilarTitlesV2.vue')
);
const TitleDetailQuickActionBarV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/sidebar/TitleDetailQuickActionBarV1.vue')
);
const AdUnit = defineAsyncComponent(() => import('@/components/AdUnit.vue'));

// need to import these immediately because it's above the fold and could cause CLS
const activeClipId = ref<string | null>(null);
const streamingChart = ref<{ renderChart?: boolean } | null>(null);
const container = ref(null);
const displayTextRecommendationModal = ref<boolean>(false);

const { newBuyboxView, buyboxExpView, isAdmin, isEditor, loggedInNewBuyboxView } = useUserStore();
const { country, language } = useLanguageStore();
const { t } = useI18n();
const { isLoggedIn } = useUserStore();




const {
	title,
	titleName,
	titleId,
	titleObjectId,
	seasonName,
	seasonNumber,
	seasonTotalEpisodeCount,
	isShow,
	isSeason,
	recentEpisodes,
	recentEpisodesAvailable,
	isTitleUpcoming,
	showDetails,
	originalTitle,
	isInWatchList,
} = useTitleDetailInfo(() => props.titleDetailNode);

const {
	isActive: isInternationalOffersActive,
	hasInternationalOffers,
	isInternationalOffersTitle,
} = useInternationalOffersStatus({
	title: () => props.titleDetailNode,
});

const { AVODOffer } = useBuyBoxData({
	title: () => props.titleDetailNode as TitleDetail,
});

const { isArbitrating } = useArbitrage();

// Grid Swiper Exp
const activateExpGridSwiper = computed(
	() =>
		!isArbitrating.value &&
		!!props.titleDetailNode &&
		!props.titleDetailNode.offers.some(offer => ['amp', 'hlu'].includes(offer.package.shortName))
	// exclude Prime and Hulu titles, because these are not compatible with the Prime SQTV / Hulu QTV placements
);
useActivateExperiment('EXP_GRID_SWIPER', activateExpGridSwiper);

// Grid Redesign Exps
const activateGridRedesign = computed(
	() => !isArbitrating.value && !!props.titleDetailNode && props.titleDetailNode.objectType === ObjectType.Movie
);

const { activeVariantWithControlGroup: gridRedesignClickActiveVariant } = useActivateExperiment(
	'EXP_GRID_REDESIGN_CLICK',
	activateGridRedesign
);

// Important - declare Grid bbox experiments before useBuyboxView, as experiments affect the return value of useBuyboxView

// OLD BUYBOX EXP
const { isGridBuyboxDefault } = useBuyboxView({ objectType: () => props.titleDetailNode?.objectType });
// OLD BUYBOX EXP

// ATV LANDING PAGE EXP

const activateATVLandingPageExp = computed(() => {
	return !(props.titleDetailNode?.offers ?? []).some(o => ['atp', 'hlu'].includes(o.package.shortName));
});

const { activeVariant: atvLandingPageActiveVariant } = useActivateExperiment(
	'EXP_ATV_LANDING_PAGE',
	activateATVLandingPageExp
);

// only countries that are enabled for international offers get the noOffer prop filled
provide('noOffer', isInternationalOffersActive.value);

const buyboxOffers = computed(() => {
	if (!props.titleDetailNode) {
		return null;
	}
	return extractBuyboxOffers(props.titleDetailNode);
});

const isListSponsoredAd = computed(() => props._sponsoredAd?.campaign?.node?.__typename === 'GenericTitleList');
const isShadowCampaign = computed(() => !!props._sponsoredAd?.bidId && props._sponsoredAd?.campaign === null);
const hasActiveSRCampaign = computed(
	() => props._sponsoredAd?.holdoutGroup == false && props._sponsoredAd?.campaign != null
);

const streamingCharts = computed(() => {
	const streamingChartsModule = props.titleDetailNode?.titleModules.find(
		module => module?.template.technicalName === 'TITLE_STREAMING_CHARTS'
	);
	return (streamingChartsModule?.content as ModuleContentStreamingCharts)?.Titles ?? [];
});

const hasNonCinemaOffers = computed(
	() => !!props.titleDetailNode?.offers.some(offer => offer.monetizationType !== MonetizationType.Cinema)
);

// here we only check if the current title has any streaming charts informations.
// it won't have informations about titles up or below the title's streaming charts' rank.
const displayStreamingChart = computed(() => {
	if (!streamingChartCountries.includes(country.value) || !props.titleDetailNode?.streamingCharts) {
		return false;
	}

	// temporary hack for broken ES logic
	if (!hasNonCinemaOffers.value) return false;

	const rank = props.titleDetailNode?.streamingCharts.edges?.[0]?.streamingChartInfo?.rank;
	return !!rank && rank < 10000;
});

// EXP_JWTV_CINEMATIC_PAGES
const hasJWTVTVOD = computed(() => {
	const watchNowOffer = props.titleDetailNode?.offers.find(
		offer => offer.monetizationType === MonetizationType.Rent && offer.package.technicalName === 'justwatchtv'
	);

	return !!watchNowOffer && isJWTVRentalOffer(watchNowOffer);
});

const hasJWTVAVOD = computed(() => {
	const offers = props.titleDetailNode?.justwatchTVOffers;
	if (!offers || offers.length === 0) return false;

	return offers[0].monetizationType === MonetizationType.Free && offers[0].package.technicalName === 'justwatchtv';
});

const { activeVariant: tvodCinematicActiveVariant } = useActivateExperiment('EXP_JWTV_CINEMATIC_PAGES', hasJWTVTVOD);
const { activeVariant: avodCinematicActiveVariant } = useActivateExperiment(
	'EXP_JWTV_AVOD_CINEMATIC_PAGES',
	hasJWTVAVOD
);

const cinematicExpActiveVariant = computed(() => {
	return tvodCinematicActiveVariant.value || avodCinematicActiveVariant.value;
});

const cinematicExpBuyBoxType = ref<NewBuyBoxView>(NewBuyBoxView.GRID);

function updateCinematicBuyBoxType(view: { buyBoxType: NewBuyBoxView }) {
	cinematicExpBuyBoxType.value = view.buyBoxType;
}
// EXP_JWTV_CINEMATIC_PAGES

const showSynopsis = computed(
	() =>
		!(
			props.metaData?.htmlContentJson?.detail_heading != null &&
			props.metaData?.htmlContentJson?.detail_content != null
		) && !!props.titleDetailNode?.content?.shortDescription
);

const editorialSynopsis = computed(() => props.titleDetailNode?.content?.editorialContent?.synopsis ?? null);

const showSimilarTitles = computed(() => !!props.titleDetailNode && !props.metaData?.htmlContent.hide_similar_titles);

const aboutHeadline = computed(() => {
	return isSeason.value
		? t('WEBAPP_ABOUT_THE_SEASON')
		: isShow.value
		? t('WEBAPP_ABOUT_THE_TV_SHOW')
		: t('WEBAPP_ABOUT_THE_MOVIE');
});

const showNewBuyBox = computed(() => {
	if (cinematicExpActiveVariant.value === 'variant_1') {
		return cinematicExpBuyBoxType.value !== NewBuyBoxView.GRID;
	}
	if (isInternationalOffersTitle.value) {
		return true;
	}

	// OLD BUYBOX EXP
	if (isGridBuyboxDefault.value) {
		return buyboxExpView.value !== NewBuyBoxView.GRID;
	}
	// OLD BUYBOX EXP

	if (isLoggedIn.value && (isSeason.value || isShow.value)) {
		return loggedInNewBuyboxView.value !== NewBuyBoxView.GRID;
	}

	return newBuyboxView.value !== NewBuyBoxView.GRID;
});

const showOffersHistory = computed(
	() =>
		!isLoggedIn.value &&
		!!props.titleDetailNode?.offersHistory?.length &&
		props.titleDetailNode.offersHistory.length > 0 &&
		!hasInternationalOffers.value
);

const episodesTitle = computed(() => {
	// only show this for now for show page release - tbc
	if (isSeason.value) return t('WEBAPP_X_EPISODES', { episodes: seasonTotalEpisodeCount.value || '' });
	return t('WEBAPP_WATCH_NEWEST_EPISODES');
	// return recentEpisodesAvailable.value ? t('WEBAPP_RECENT_EPISODES') : t('WEBAPP_WATCH_NEWEST_EPISODES');
});

const maxEpisodes = computed(() =>
	recentEpisodesAvailable.value ? recentEpisodes.value.length : seasonTotalEpisodeCount.value
);

const episodes = computed(() =>
	recentEpisodesAvailable.value
		? recentEpisodes.value
		: (props.titleDetailNode as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Season'>).episodes || []
);

const showTextRecommendationTools = computed(() => isAdmin.value || isEditor.value);

// we don't want to display too many ads in one viewport.
// that's why we're taking out the 3rd ad slot, if it's:
// - on a page without streaming charts (that would push down the content vertically)
// - only in DE
const displayThirdAd = computed(() => {
	return !['DE'].includes(country.value) || displayStreamingChart.value;
});

const titleClips = computed(() => {
	// check for localised trailers, if not use fallbacks
	const localisedClips = getTitleClips(props.titleDetailNode, 'content');
	if (localisedClips.length) return localisedClips;

	return getTitleClips(props.titleDetailNode, 'fallBackClips');
});

const hasTrailers = computed(() => titleClips.value.length > 0);

function getTitleClips(titleNode: typeof props.titleDetailNode, key: 'content' | 'fallBackClips') {
	const youtubeClips = titleNode?.[key]?.clips ?? [];
	if (youtubeClips.length) return youtubeClips;

	const dailymotionClips = titleNode?.[key]?.dailymotionClips ?? [];
	return dailymotionClips.length ? [dailymotionClips[0]] : [];
}

const { trackWtw } = useWtwTracking({ title: () => props.titleDetailNode });

const promotedBundles = computed(() => props.titleDetailNode?.promotedBundles ?? []);
const hasPromotedOffers = computed(() => props.promotedOffers.length + promotedBundles.value.length > 0);

function openTextRecommendationModal() {
	// if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	trackWtw('modalOpened');

	displayTextRecommendationModal.value = true;
	disableScroll();
}

function closeTextRecommendationModal() {
	displayTextRecommendationModal.value = false;
	enableScroll();
}

const textRecommendationArray = computed(() => {
	const textRecs = props.titleDetailNode?.content?.textRecommendations ?? [];

	if (isLoggedIn.value) return textRecs;

	// hide user text recommendations for non-logged-in users, for SEO
	return textRecs.filter(textRec => ![ProfileType.User, ProfileType.ProUser].includes(textRec.profile.profileType));
});

const visibleSectionState = computed(() => {
	return {
		trailers: !!(hasTrailers.value && props.titleDetailNode),
		'why-to-watch': textRecommendationArray.value?.length > 0 && isLoggedIn.value,
		synopsis: showSynopsis.value || editorialSynopsis != null,
		'streaming-chart': displayStreamingChart.value && !!streamingChart.value?.renderChart,
		'episodes-lists': !isTitleUpcoming.value && (isSeason.value || isShow.value),
		'regular-buybox': !(isTitleUpcoming.value && !showNewBuyBox.value),
		'similar-content': showSimilarTitles.value,
	};
});

return { __sfc: true,TVODHeroSection, AVODHeroSection, TitleDetailStreamingChartV2, TitleSynopsis, ArticleBlock, UpcomingContent, EpisodesList, TitleCredits, SpinningText, TitleDetailSimilarTitlesV2, TitleDetailQuickActionBarV1, AdUnit, activeClipId, streamingChart, container, displayTextRecommendationModal, newBuyboxView, buyboxExpView, isAdmin, isEditor, loggedInNewBuyboxView, country, language, t, isLoggedIn, emits, props, title, titleName, titleId, titleObjectId, seasonName, seasonNumber, seasonTotalEpisodeCount, isShow, isSeason, recentEpisodes, recentEpisodesAvailable, isTitleUpcoming, showDetails, originalTitle, isInWatchList, isInternationalOffersActive, hasInternationalOffers, isInternationalOffersTitle, AVODOffer, isArbitrating, activateExpGridSwiper, activateGridRedesign, gridRedesignClickActiveVariant, isGridBuyboxDefault, activateATVLandingPageExp, atvLandingPageActiveVariant, buyboxOffers, isListSponsoredAd, isShadowCampaign, hasActiveSRCampaign, streamingCharts, hasNonCinemaOffers, displayStreamingChart, hasJWTVTVOD, hasJWTVAVOD, tvodCinematicActiveVariant, avodCinematicActiveVariant, cinematicExpActiveVariant, cinematicExpBuyBoxType, updateCinematicBuyBoxType, showSynopsis, editorialSynopsis, showSimilarTitles, aboutHeadline, showNewBuyBox, showOffersHistory, episodesTitle, maxEpisodes, episodes, showTextRecommendationTools, displayThirdAd, titleClips, hasTrailers, getTitleClips, trackWtw, promotedBundles, hasPromotedOffers, openTextRecommendationModal, closeTextRecommendationModal, textRecommendationArray, visibleSectionState, SimilarTitles, BuyBoxBanner, ListBuyBox, SectionContainer, TitleDetailJumpLinksV1, TitleDetailTitleClipsV1, isTitleSeason, isTitleShow, TitleDetailHeroV1, TitleDetailTitleSidebarV1, BuyBoxTypeSwitcher, GridBuyBox, PromotedGridBuyBox, TextRecommendationModalV2, TextRecommendations, SeasonListV1, StreamingOffersHistory }
}

})